import { PATH } from '../const/path';
import { CONTEXT_TYPE } from './context-type';
import { PROFILE_ROLE } from './profile-role';

export interface RouteAccess {
    /** Any one of these is enough. */
    roles: string[];
    /** Set only where the roles alone do not identify the area - `administrator` spans two of them. */
    contextType?: string;
}

// Which roles may open each screen, in one table because two different mechanisms need the answer and
// neither can cover the other's case:
//
//  - `roleGuard` covers navigation inside the running app, where the profile is loaded;
//  - `LayoutComponent.getLandingUrl` covers a hand-typed URL, a bookmark and a reload. A guard is
//    useless there: it is evaluated before `LayoutComponent` is created, and the profile is fetched in
//    that component's `ngOnInit` - so on a full page load every guard sees an unresolved profile. (A
//    guard that waited for it would deadlock, for the same reason.)
//
// Keyed by the full path so the landing check can look a URL up directly, rather than reading `data`
// off a lazily-loaded route config it would have to walk. A path that is absent is unrestricted.
export const ROUTE_ACCESS: Readonly<Record<string, RouteAccess>> = {
    [PATH.context_customer_user_access]: { roles: [PROFILE_ROLE.subscriberAdministrator] },
    [PATH.context_customer_on_premises_integrations]: { roles: [PROFILE_ROLE.subscriberAdministrator] },
    [PATH.context_company_user_access]: { roles: [PROFILE_ROLE.companyAdministrator] },
    [PATH.context_distributor_user_access]: { roles: [PROFILE_ROLE.buAccessAdministrator] },
    [PATH.context_distributor_subscribers_user_access]: { roles: [PROFILE_ROLE.buSubscriberViewer] },
    // This one needs the context type: `administrator` is carried by Customer contexts too, and theirs
    // land on Organization › User (below), not here.
    [PATH.context_distributor_product]: { roles: [PROFILE_ROLE.administrator], contextType: CONTEXT_TYPE.distributor },
    // Company_Move_Responsible is carried by Distributor contexts only; the type is stated anyway so the
    // screen stays a Distributor's should the role ever turn up elsewhere.
    [PATH.context_distributor_company_move]: { roles: [PROFILE_ROLE.companyMoveResponsible], contextType: CONTEXT_TYPE.distributor },
    // The same role from the other side: a Kalmar customer's Administrator. The context type keeps a
    // Distributor, which carries Administrator as well, out of a customer's users and companies.
    [PATH.context_organization_user]: { roles: [PROFILE_ROLE.administrator], contextType: CONTEXT_TYPE.customer },
    [PATH.context_organization_company]: { roles: [PROFILE_ROLE.administrator], contextType: CONTEXT_TYPE.customer }
};

/**
 * Strips trailing slashes, so `/context/distributor/product/` looks the table up as
 * `/context/distributor/product` rather than missing it and being waved through.
 *
 * An index scan, not `replace(/\/+$/, '')`: that pattern is quadratic on a path ending in a long run
 * of slashes followed by anything else, because every start position inside the run consumes the rest
 * of it and then backtracks slash by slash against the anchor (S5852). The input is this browser's own
 * address bar, so the blast radius was never more than the user's own tab - but a linear scan costs
 * nothing and leaves nothing to argue about.
 */
export function trimTrailingSlashes(pathname: string): string {
    let end = pathname.length;
    while (end > 0 && pathname[end - 1] === '/') {
        end--;
    }
    return pathname.slice(0, end);
}

/**
 * Whether `pathname` may be opened with these roles. Unknown paths are allowed: this table lists the
 * screens that are role-gated, not every route in the app.
 *
 * The normalisation is done here rather than by each caller, because the two that exist arrive at it
 * differently - the guard from `state.url`, the shell from `window.location` - and a table consulted
 * by both is only worth having if they cannot disagree about which key they are asking for.
 */
export function hasAccessToPath(pathname: string, roles: string[], contextType?: string): boolean {
    const access = ROUTE_ACCESS[trimTrailingSlashes(pathname)];
    if (!access) {
        return true;
    }
    if (access.contextType && access.contextType !== contextType) {
        return false;
    }
    return access.roles.some(role => roles.includes(role));
}

/**
 * Whether the screen at `url` shows its entities read-only for these roles. Today that is one screen:
 * the subscribers screen, where a BU subscriber viewer looks at another customer's data and may change
 * none of it.
 *
 * One rule for two readers that must agree: the details panel, which is where it is enforced, and the
 * lists, whose row link would otherwise say "Edit" for details that then open read-only. An exact match
 * on the URL, as the details panel always did.
 */
export function isReadOnlyScreen(url: string, roles: string[]): boolean {
    return roles.includes(PROFILE_ROLE.buSubscriberViewer) && url === PATH.context_distributor_subscribers_user_access;
}
